/**
 * Public customer review form — opened from the link emailed in Stage 7.
 * No auth; one-time-use token. Customers leave 1–5 stars + a written review.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export default function PublicReview() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState('');
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/public/review/${token}`)
      .then(async r => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(formatApiError(d) || 'Link invalid');
        setCtx(d);
      }).catch(e => setErr(e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) return setErr('Please pick a star rating.');
    if (!reviewText.trim()) return setErr('Please write a short review.');
    setErr(''); setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/review/${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, review_text: reviewText.trim() }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed');
      setDone(true);
    } catch (e) { setErr(e.message); }
    finally { setSubmitting(false); }
  };

  if (err) return <Wrap><p data-testid="review-error" style={{ color: '#b91c1c' }}>{err}</p></Wrap>;
  if (!ctx) return <Wrap><p>Loading…</p></Wrap>;
  if (done) return <Wrap><h2 style={{ marginTop: 0 }}>Thank you 🙌</h2><p>Your review has been sent to the Ox Resin team. We really appreciate it.</p></Wrap>;

  return (
    <Wrap>
      <div data-testid="review-context" style={{ fontSize: 11, letterSpacing: 2, color: '#888', textTransform: 'uppercase' }}>OX RESIN · Customer Review</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '6px 0 4px' }}>How did {ctx.installer_name} do?</h1>
      {ctx.project_name && <p style={{ color: '#555', fontSize: 13, marginTop: 0 }}>Project: <strong>{ctx.project_name}</strong></p>}
      <form onSubmit={submit} style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }} data-testid="review-stars">
          {[1,2,3,4,5].map(n => (
            <button type="button" key={n} onClick={() => setRating(n)} data-testid={`star-${n}`} style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: 4 }}>
              <Star style={{ width: 32, height: 32, color: n <= rating ? '#f59e0b' : '#d1d5db', fill: n <= rating ? '#f59e0b' : 'none' }} />
            </button>
          ))}
        </div>
        <label style={{ fontSize: 12, fontWeight: 700, color: '#1f4030' }}>Your review *</label>
        <textarea
          data-testid="review-text"
          value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={5}
          placeholder="What did they do well? Anything they could improve?"
          style={{ width: '100%', marginTop: 6, padding: 11, border: '1px solid #d8d0bd', borderRadius: 8, fontSize: 14, fontFamily: 'inherit' }}
        />
        {err && <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>{err}</div>}
        <button type="submit" disabled={submitting} data-testid="review-submit"
          style={{ marginTop: 16, padding: '13px 18px', background: '#D4A24B', color: '#0B0B0E', border: 0, borderRadius: 10, fontWeight: 800, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Sending…' : 'Submit review'}
        </button>
      </form>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f6f5f0', padding: '24px 16px', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', background: '#fff', padding: '28px 26px', borderRadius: 14, border: '1px solid #eadfca' }}>{children}</div>
    </div>
  );
}
